import React, { useContext, useEffect, useState } from 'react';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { formatMoney, useLocalTable } from '../finanzas-cxp/shared.js';

/**
 * V1: `IbuFront/src/components/views/Reportes/ReporteTransferencias.jsx`, gateada
 * por `user.transferenciasReporte` -> `reportes:transferencias`. Fuente: Cargos del
 * rango de fechas con `comisionNumero > 0`, agrupados por cliente (backend nuevo:
 * `GET /reportes/transferencias/:fechaInicio/:fechaFin`, ver
 * `reportes-extra.service.js#transferencias`). V1 exportaba PDF/Excel/eMail -- no se
 * replica aquí: ningún reporte de V2 (Análisis, Gráficas, Contenedores) tiene esas
 * exportaciones todavía, así que agregarlas solo aquí sería inconsistente; queda
 * documentado en DUDAS.md como brecha general de los reportes, no propia de esta
 * pantalla.
 */
export default function ReporteTransferenciasPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('reportes:transferencias');

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  const [fechaInicio, setFechaInicio] = useState(inicio.toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState(hoy.toISOString().slice(0, 10));

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  useEffect(() => {
    if (!puedeVer || !fechaInicio || !fechaFin) return;
    setCargando(true);
    setError(null);
    apiClient
      .get(`/reportes/transferencias/${fechaInicio}/${fechaFin}`)
      .then(({ data }) => setFilas((data.data || []).map((f) => ({ ...f, _id: f.idCliente || f.cliente }))))
      .catch((e) => { setError(e.message); setFilas([]); })
      .finally(() => setCargando(false));
  }, [puedeVer, fechaInicio, fechaFin]);

  const { search, setSearch, page, setPage, pageRows, filtradas, sortBy, sortOrder, ordenarPor, pagination } = useLocalTable(filas, {
    searchFields: ['cliente', 'total'],
    perPage: 50,
  });

  const total = filtradas.reduce((t, f) => t + (f.total || 0), 0);

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const columnas = [
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'total', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.total) },
  ];

  const columnasExport = [
    { key: 'cliente', label: 'Cliente' },
    { key: 'total', label: 'Importe', render: (r) => formatMoney(r.total) },
  ];
  const columnasExcel = columnasExport.map((c) => ({ key: c.key, label: c.label }));
  const opcionesPdf = {
    fileName: 'ReporteTransferencias',
    totalCols: { cliente: 'Total', total: formatMoney(total) },
  };
  const hacerPdf = () => exportTablePdf('Reporte de Transferencias', columnasExport, filtradas, opcionesPdf);
  const hacerExcel = () => exportTableExcel('Reporte de Transferencias', columnasExcel, filtradas, {
    fileName: `ReporteTransferencias-${fechaInicio}-${fechaFin}`, sheetName: 'ReporteTransferencias',
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Transferencias</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={hacerPdf} className="btn-outline text-xs px-3 py-1.5">PDF</button>
          <button type="button" onClick={() => setMailOpen(true)} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={hacerExcel} className="btn-outline text-xs px-3 py-1.5">Excel</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="input-base" />
        </div>
        <div className="ml-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="input-base w-60" />
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable
        columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} rowKey="idCliente"
        footer={{ cliente: 'TOTAL', total: formatMoney(total) }}
      />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Reporte de Transferencias"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Reporte de Transferencias', columnasExport, filtradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Reporte de Transferencias', email, fileName: 'ReporteTransferencias.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el Reporte de Transferencias.'),
          });
        }}
      />
    </div>
  );
}
